'use client'
import ExperienceGridCard, {
	ExperienceGridCardProps,
} from './ExperienceGridCard'
import {Button, SlideshowNative, SmallText} from '@local/ui/components'
import clsx from 'clsx'
import {useSearchParams, useRouter} from 'next/navigation'
import {useMemo, useRef, useState} from 'react'

type ExperienceType =
  | 'group-retreat'
  | 'private-journey'
  | 'kaer-for-business'
  | 'all'
  | 'destination';
interface ExperienceGridContentProps {
	title: string;
	surtitle: string;
	grid: ExperienceGridCardProps[];
	forHome?: boolean;
}

export const ExperienceGridContent = ({
	title,
	surtitle,
	grid,
	forHome = false,
}: ExperienceGridContentProps) => {
	const router = useRouter()
	const slideshowRef = useRef<HTMLDivElement>(null)
	const [selectedTab, setSelectedTab] = useState<ExperienceType | null>(null)
	const searchParams = useSearchParams()
	const destination = searchParams.get('destination')
	const tripType = searchParams.get('tripType')
	const guestCounts = searchParams.get('guestCounts')
	const fromHomeTab = searchParams.get('fromHomeTab')

	const onTabChange = (tab: ExperienceType) => {
		router.push(`/experiences?tripType=${tab}&fromHomeTab=true`)
	}

	const filteredGrid = useMemo(() => {
		let result = grid

		if (destination) {
			result = grid.filter((item) => {
				if (tripType === 'kaer-for-business') {
					return true
				}
				if (!item?.location) return false

				return item?.location.toLowerCase().replaceAll(' ', '-').includes(destination.toLowerCase().replaceAll(' ', '-'))
			})
		}
		if (selectedTab) {
			return result.filter(
				(item) =>
					item.experienceType?.toLowerCase().replaceAll(' ', '-') ===
          selectedTab.toLowerCase(),
			)
		}
		if (tripType) {
			return result.filter(
				(item) =>
					item.experienceType?.toLowerCase().replaceAll(' ', '-') ===
          tripType.toLowerCase(),
			)
		}
		if (guestCounts) {
			return result.filter(
				(item) => item.maxPeopleCount || parseInt(guestCounts) <= 12,
			)
		}
		return result
	}, [grid, selectedTab, destination, tripType, guestCounts])

	return (
		<div className='px-10 flex flex-col items-center'>
			{forHome
				? (
					<div className=' flex flex-col items-center py-50 lg:py-100'>
						<SmallText>{title}</SmallText>
						<h3 className='font-serif font-medium text-28  lg:text-34 text-center text-black'>
							{surtitle}
						</h3>
						<div className='flex flex-row gap-8 mt-34'>
							<Button label='Group Retreats' className='text-9' variant='secondary-transparent' onClick={() => onTabChange('group-retreat')} small />
							<Button label='Private Journeys' className='text-9' variant='secondary-transparent' onClick={() => onTabChange('private-journey')} small />
							<Button label='Kaer For Business' className='text-9' variant='secondary-transparent' onClick={() => onTabChange('kaer-for-business')} small />
						</div>
					</div>
				)
				: (
					<div className='flex flex-col items-center py-50 lg:py-60'>
						{filteredGrid.length > 0 &&	<SmallText>{filteredGrid.length} experiences available</SmallText>}
						{filteredGrid.length === 0 && (
							<div className='flex flex-col items-center'>
								<SmallText>Nothing listed here yet</SmallText>
								<h3 className='font-serif font-medium text-18  lg:text-24 text-center text-black'>
									We can craft a custom Kaer experience for you—anywhere in the world
								</h3>
								<Button
									small
									label='Start Planning'
									className='mt-20'
									variant='primary'
									onClick={() => {
										router.push('/experience/custom-experience')
									}} />
							</div>
						)}
						{fromHomeTab && (
							<Button
								small
								label='Reset Filters'
								className='mt-20'
								variant='secondary'
								onClick={() => {
									router.push('/')
								}} />
						)}
					</div>
				)}
			{forHome &&
				<SlideshowNative
					disableObserver
					ref={slideshowRef}
					className='span-w-12-wider lg:span-w-7-wide gutter-gap-1 lg-max:scroll-pl-[var(--margin)] lg-max:margin-px-1 lg:margin-pr-1 lg:hidden'>
					{filteredGrid.map((card, index) => (
						<ExperienceGridCard key={index} {...card} onlyMobile />
					))}
				</SlideshowNative>}
			<div className={clsx('grid gap-20 auto-rows-auto grid-cols-[repeat(auto-fit,minmax(335px,1fr))] xl:grid-cols-4 ', forHome && 'lg-max:hidden')}>
				{filteredGrid.map((item) => (
					<ExperienceGridCard key={item.slug} {...item} />
				))}
			</div>

			{forHome && (
				<Button
					className='lg:mt-50 mt-20 border border-black text-12 font-medium text-black'
					variant='secondary-transparent'
					href='/experiences'
					label='All Experiences' />
			)}
		</div>
	)
}
